import React, { useState, useRef, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { NAVIGATION_ITEMS, APP_NAME, CHIP_LOGO } from '../constants.ts';
import { 
  Menu, 
  Search, 
  LogOut, 
  ArrowLeft, 
  Moon, 
  Sun,
  MoreHorizontal,
  UserCircle,
  Settings,
  Users,
  Bell
} from 'lucide-react';
import { useAuth } from '../context/AuthContext.tsx';
import { useTheme } from '../context/ThemeContext.tsx';
import { useStore } from '../context/StoreContext.tsx';
import NotificationCenter from './NotificationCenter.tsx';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { userProfile, notifications } = useStore();
  
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  
  const menuRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.read).length;

  // Determine which avatar to show: Firestore Profile (Base64) > Auth Profile (URL)
  const displayPhotoURL = userProfile?.photoURL || user?.photoURL;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const UserMenu = () => (
    <div className="absolute bottom-full left-0 w-full mb-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-xl shadow-xl overflow-hidden min-w-[200px] z-50 animate-[fadeIn_0.1s_ease-out]">
      <div className="p-1">
        <button 
          onClick={() => { navigate('/settings'); setIsProfileMenuOpen(false); }}
          className="w-full text-left px-3 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] rounded-lg flex items-center gap-2 transition-colors"
        >
          <UserCircle size={18} className="text-[var(--text-secondary)]" />
          Edit Profile
        </button>
        <button 
          onClick={() => { navigate('/settings'); setIsProfileMenuOpen(false); }}
          className="w-full text-left px-3 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] rounded-lg flex items-center gap-2 transition-colors"
        >
          <Users size={18} className="text-[var(--text-secondary)]" />
          Team
        </button>
        <button 
          onClick={() => { navigate('/settings'); setIsProfileMenuOpen(false); }}
          className="w-full text-left px-3 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] rounded-lg flex items-center gap-2 transition-colors"
        >
          <Settings size={18} className="text-[var(--text-secondary)]" />
          Settings
        </button>
        <div className="h-px bg-[var(--border-subtle)] my-1"></div>
        <button 
          onClick={handleLogout}
          className="w-full text-left px-3 py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg flex items-center gap-2 transition-colors"
        >
          <LogOut size={18} />
          Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[var(--bg-secondary)] overflow-hidden transition-colors duration-200 p-3 gap-3">
      {/* Floating Sidebar Desktop */}
      <aside 
        className={`hidden md:flex flex-col bg-[var(--bg-primary)] rounded-2xl shadow-xl border border-[var(--border-subtle)] transition-all duration-300 ease-in-out relative z-30
          ${isSidebarCollapsed ? 'w-[80px]' : 'w-[280px]'}
        `}
      >
        {/* Logo Section */}
        <div className={`h-20 flex items-center ${isSidebarCollapsed ? 'justify-center' : 'px-6'} transition-all flex-shrink-0 overflow-hidden`}>
           <img 
            src={CHIP_LOGO} 
            alt="Logo" 
            className="w-10 h-10 rounded-lg object-contain flex-shrink-0" 
            referrerPolicy="no-referrer"
           />
           <span className={`ml-3 font-heading font-bold text-lg text-[var(--text-primary)] whitespace-nowrap transition-all duration-200 ${isSidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
             {APP_NAME}
           </span>
        </div>

        {/* Collapsible Content */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 space-y-1 scrollbar-hide py-2">
          {NAVIGATION_ITEMS.map((item, index) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            
            // Logic to determine if we need a section header
            const prevItem = index > 0 ? NAVIGATION_ITEMS[index - 1] : null;
            const showSection = !prevItem || prevItem.section !== item.section;

            return (
              <React.Fragment key={item.path}>
                {showSection && !isSidebarCollapsed && (
                  <div className="px-3 pt-4 pb-2 text-[10px] uppercase font-bold text-[var(--text-tertiary)] tracking-wider">
                    {item.section}
                  </div>
                )}
                {showSection && isSidebarCollapsed && (
                   <div className="h-px w-8 mx-auto bg-[var(--border-subtle)] my-2"></div>
                )}
                
                <Link
                  to={item.path}
                  className={`group flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all relative
                    ${isActive 
                      ? 'text-[var(--accent-primary)] bg-[var(--bg-secondary)]' 
                      : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]'
                    }
                    ${isSidebarCollapsed ? 'justify-center' : ''}
                  `}
                >
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-[var(--accent-primary)] rounded-r-full" />
                  )}
                  
                  <Icon size={22} className="flex-shrink-0" />
                  
                  <span className={`whitespace-nowrap transition-all duration-200 ${isSidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
                    {item.name}
                  </span>
                  
                  {/* Tooltip for collapsed state */}
                  {isSidebarCollapsed && (
                    <div className="absolute left-full ml-4 px-3 py-1.5 bg-[var(--text-primary)] text-[var(--bg-primary)] text-xs rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                      {item.name}
                    </div>
                  )}
                </Link>
              </React.Fragment>
            );
          })}
        </nav>

        {/* User Profile Tray */}
        <div className="p-4 mt-auto border-t border-[var(--border-subtle)] bg-[var(--bg-primary)] rounded-b-2xl relative">
          
          <div className={`flex flex-col gap-4 ${isSidebarCollapsed ? 'items-center' : ''}`}>
            
            {/* Controls Row (Theme & Notifications) */}
            <div className={`flex items-center ${isSidebarCollapsed ? 'flex-col-reverse gap-4' : 'justify-between px-1'} transition-all relative`}>
               {/* Theme Toggle */}
               {isSidebarCollapsed ? (
                 <button 
                   onClick={toggleTheme}
                   className="w-10 h-10 flex items-center justify-center rounded-full bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                   title="Toggle Theme"
                 >
                   {theme === 'dark' ? <Moon size={20} /> : <Sun size={20} />}
                 </button>
               ) : (
                 <div className="flex items-center gap-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" checked={theme === 'dark'} onChange={toggleTheme} />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-[var(--accent-primary)]"></div>
                      <span className="ml-2 text-xs font-medium text-[var(--text-secondary)] flex items-center gap-1.5">
                        {theme === 'dark' ? <Moon size={14} /> : <Sun size={14} />}
                      </span>
                    </label>
                 </div>
               )}

               {/* Notification Bell */}
               <div ref={notifRef} className="relative">
                  <button 
                    onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                    className={`relative w-10 h-10 flex items-center justify-center rounded-full transition-colors ${isNotificationsOpen ? 'bg-[var(--bg-tertiary)] text-[var(--text-primary)]' : 'hover:bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
                  >
                      <Bell size={20} />
                      {unreadCount > 0 && (
                        <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-[var(--accent-primary)] border-2 border-[var(--bg-primary)] rounded-full"></span>
                      )}
                  </button>
                  {isNotificationsOpen && <NotificationCenter onClose={() => setIsNotificationsOpen(false)} />}
               </div>
            </div>

            {/* Profile Row */}
            <div className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : 'justify-between gap-3'} transition-all relative`} ref={menuRef}>
               {isProfileMenuOpen && <UserMenu />}
               
               <button 
                onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                className="flex items-center gap-3 flex-1 text-left group"
               >
                 <div className="relative">
                   {displayPhotoURL ? (
                     <img 
                       src={displayPhotoURL} 
                       alt="Profile" 
                       className="w-10 h-10 rounded-full object-cover shadow-md border border-[var(--border-subtle)] flex-shrink-0" 
                     />
                   ) : (
                     <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--accent-primary)] to-purple-600 text-white flex items-center justify-center font-bold text-sm shadow-md flex-shrink-0 group-hover:shadow-lg transition-all">
                        {user?.email?.charAt(0).toUpperCase()}
                     </div>
                   )}
                   <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 border-2 border-[var(--bg-primary)] rounded-full"></div>
                 </div>
                 
                 {!isSidebarCollapsed && (
                   <div className="overflow-hidden flex-1">
                     <p className="text-sm font-bold text-[var(--text-primary)] truncate">{user?.displayName || 'Creator'}</p>
                     <p className="text-xs text-[var(--text-tertiary)] truncate">{userProfile?.role || 'Team Member'}</p>
                   </div>
                 )}
               </button>

               {!isSidebarCollapsed && (
                 <button 
                  onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  className="p-1.5 rounded-lg text-[var(--text-tertiary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)] transition-colors"
                 >
                    <MoreHorizontal size={20} />
                 </button>
               )}
            </div>

          </div>
        </div>

        {/* Collapse Toggle Button - Floating in the gap */}
        <button 
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          className="absolute -right-3 top-20 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-full p-1.5 text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:border-[var(--text-primary)] transition-all shadow-md z-40"
        >
          <ArrowLeft size={14} className={`transition-transform duration-300 ${isSidebarCollapsed ? 'rotate-180' : ''}`} />
        </button>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 bg-[var(--bg-secondary)] relative rounded-2xl overflow-hidden">
        
        {/* Header - Transparent/Glass */}
        <header className="h-20 flex items-center justify-between px-8 sticky top-0 z-20 backdrop-blur-sm bg-[var(--bg-secondary)]/80">
          <div className="flex items-center gap-4">
            <button 
              className="md:hidden p-2 text-[var(--text-secondary)]"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <Menu size={24} />
            </button>
          </div>

          <div className="flex items-center gap-4">
             {/* Search Bar in Header */}
            <div className="hidden md:flex items-center relative">
               <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" size={18} />
               <input 
                  type="text" 
                  placeholder="Search..." 
                  className="pl-10 pr-4 py-2.5 bg-[var(--bg-primary)] border-none shadow-sm rounded-full text-sm text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:ring-1 focus:ring-[var(--border-default)] w-64 transition-all"
                />
            </div>
          </div>
        </header>

        {/* Content Scroll Area */}
        <main className="flex-1 overflow-y-auto px-8 pb-8">
          {children}
        </main>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)}></div>
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-[var(--bg-primary)] shadow-2xl flex flex-col animate-[slideIn_0.2s_ease-out]">
            <div className="h-20 flex items-center px-6 justify-between">
               <span className="font-heading font-bold text-xl text-[var(--text-primary)]">{APP_NAME}</span>
               <button onClick={() => setIsMobileMenuOpen(false)}><ArrowLeft /></button>
            </div>
            <nav className="flex-1 py-4 px-3 overflow-y-auto">
               <ul className="space-y-1">
                {NAVIGATION_ITEMS.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium ${
                        location.pathname === item.path 
                        ? 'bg-[var(--bg-secondary)] text-[var(--text-primary)]' 
                        : 'text-[var(--text-secondary)]'
                      }`}
                    >
                      <item.icon size={22} />
                      {item.name}
                    </Link>
                  </li>
                ))}
               </ul>
            </nav>
            <div className="p-4 space-y-3 border-t border-[var(--border-subtle)]">
              <button onClick={toggleTheme} className="flex items-center gap-3 text-sm text-[var(--text-secondary)] px-3 py-2">
                 {theme === 'dark' ? <Moon size={18} /> : <Sun size={18} />} Switch Theme
              </button>
              <button 
                onClick={handleLogout}
                className="flex items-center gap-3 text-sm text-[var(--text-secondary)] hover:text-red-500 px-3 py-2"
              >
                <LogOut size={18} /> Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;